import _ from 'lodash';
import classNames from 'classnames';
import sanitizeHtml from 'sanitize-html';
import React, { Component, PropTypes } from 'react';
import { Modal } from 'react-bootstrap';
import Timestamp from '../Timestamp';
import Image from 'components/Image';
import VideoThumbnail from 'components/media/VideoThumbnail';
import Video from 'components/media/Video';
import ComponentTitle from '../shared/ComponentTitle';
import SocialIcon from '../social/SocialIcon';
import { TITLE_TYPE } from '../../constants/FRNBaseStyles';
import convert from 'xml-js';

/**
 * Class representing the meteorologists written forecast for the current day.
 *
 * Dependent on feed - station_weather_feed
 *
 */
class MeteorologistForecast extends Component {
  static propTypes = {
    FRN_rawResponses: PropTypes.array,
    backgroundColor: PropTypes.string,
    backgroundUri: PropTypes.string,
    textColor: PropTypes.string,
    weatherVideo: PropTypes.object,
    showOnUserLocation: PropTypes.bool,
    autoPlayVideo: PropTypes.bool,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    titleType: PropTypes.oneOf(Object.values(TITLE_TYPE))
  }

  static contextTypes = {
    location: PropTypes.object.isRequired,
    config: PropTypes.object.isRequired
  }

  defaultProps = {
    backgroundColor: '#FFF',
    textColor: '#000000',
    showOnUserLocation: false,
    titleType: TITLE_TYPE.MODULE_TITLE
  }

  state = {
    showModal: false
  }

  shouldComponentUpdate(nextProps) {
    return Array.isArray(nextProps.FRN_rawResponses) && nextProps.FRN_rawResponses.length > 0 ? true : false;
  }

  _setModalVisible() {
    this.setState({
      showModal: true
    });
  }

  _setModalHidden() {
    this.setState({
      showModal: false
    });
  }

  _cleanXmlObj(entry) {
    let item = Array.isArray(entry) ? entry[0] : entry;

    try {
      let cleanup = (obj) => obj ? Object.assign({}, ...obj._attributes, {"@url": obj._attributes.url}) : obj;

      let mediaContent = item["media:group"] && item["media:group"]["media:content"] 
          ? item["media:group"]["media:content"] 
          : {};

      if (Array.isArray(mediaContent)) {
        mediaContent = mediaContent.map(cleanup);
      } else if (typeof mediaContent === 'object') {
        mediaContent = cleanup(mediaContent);
      }

      return {
        ...item,
        "media:group": {"media:content": mediaContent},
        "media:content": mediaContent,
        "media:thumbnail": item["media:thumbnail"].map(cleanup),
        "media:title": item["media:title"]._cdata,
        "wn:clipid:": item["wn:clipid"]._text,
      };
    } catch (error) {
      console.log('>>> ERROR', error);
      return item;
    }
  }

  render() {
    if (!this.props.FRN_rawResponses 
        || this.props.FRN_rawResponses.length === 0
        || !this.props.FRN_rawResponses[0]) {
      return null;
    }

    let data = {};
    let {data: 
            {features: forecastData = []}  = {}
        } = this.props.FRN_rawResponses[0];

    let weatherVideoItem = null;

    let isVideoExisted = () => Array.isArray(this.props.FRN_rawResponses) && this.props.FRN_rawResponses.length >= 2;

    if (isVideoExisted()) {
      let dataStr = this.props.FRN_rawResponses[1].data;
      try {
        if (dataStr && typeof dataStr === 'string') {
          data = convert.xml2js(dataStr, {compact: true});
          weatherVideoItem = this._cleanXmlObj(data.rss.channel.item);
        } else {
          weatherVideoItem = this.props.FRN_rawResponses[1].data.rss.channel.item;
        }
      } catch (error) {
        console.log('>>> ERROR: ', error);
      }
    }

    const {
      backgroundUri,
      backgroundColor = '#FFFFFF',
      textColor = '#000000',
      autoPlayVideo = false,
      showOnUserLocation,
      timestampOptions = {},
      titleType
    //   FRN_rawResponses: [
    //     { data:
    //       { features:
    //         forecastData = []
    //         } = {}
    //       } = {},
    //     {
    //       data: {
    //         rss: {
    //           channel: {
    //             item: weatherVideoItem
    //             } = {}
    //           } = {}
    //         } = {}
    //       } = {}
    //     ] = []
      } = this.props;

    const { showModal } = this.state;
    const { config, location } = this.context;
    const weatherVideo = _.isArray(weatherVideoItem) ? weatherVideoItem[0] : weatherVideoItem;
    if (weatherVideo && typeof weatherVideo === 'object' && "wn:clipid" in weatherVideo 
        && typeof weatherVideo["wn:clipid"] === 'object' && '_text' in weatherVideo["wn:clipid"]) {
      weatherVideo["wn:clipid"] = weatherVideo["wn:clipid"]._text;
    }

    const { affiliate: { locations: stationLocations = [] } = {} } = config;
    const weatherData = _.find(forecastData, {type: 'weather'}) || {};
    const {
      'header': forecastTitle = 'Today\'s Forecast',
      'currentconditions': forecastDescription = '',
      'lastupdatedateutc': pubDate = '',
      'byline': bylineInfo = {} } = weatherData;
    const name = _.get(bylineInfo, 'firstname', "") + ' ' + _.get(bylineInfo, 'lastname', '');
    const meteorologistTitle = _.get(bylineInfo, 'title', '');
    const meteorologistImage = _.get(bylineInfo, 'profileimage', '');
    const email = _.get(bylineInfo, 'emailaddress', '');
    const { socialnetworksinfo: {
      facebook: facebookHandle = '',
      twitter: twitterHandle = '',
      google: googlePlusId = ''} = {} } = bylineInfo;
    const facebookInfo = facebookHandle ? 'https://www.facebook.com/' + facebookHandle : '';
    const twitterInfo = twitterHandle ? 'https://twitter.com/' + twitterHandle : '';
    const googleInfo = googlePlusId ? 'https://plus.google.com/' + googlePlusId : '';
    // We reinstate escaped characters removed in the parsing process, and strip out any custom styles added in producer
    const unescapedSanitizedDescription = sanitizeHtml(_.unescape(forecastDescription));
    const profileInfoClassNames = classNames(
      'MeteorologistForecast-profileInfo',
      {
        'MeteorologistForecast-profileInfo--belowVideo': weatherVideo
      }
    );
    const { showElapsedTime, displayShortDateTime } = timestampOptions;

    let customComponentStyles = {};
    let customTextUnderlayStyles = {};

    // If the current location isn't a default location in the config, then hide the meteorologist report since it's not
    // applicable for the selected location. This can be overriden by setting showOnUserLocation to true in the config
    if (!showOnUserLocation && !_.find(stationLocations, { 'zipcode': Number(location.zipcode)})){
      return null;
    }

    customComponentStyles['color'] = textColor;
    customComponentStyles['backgroundColor'] = backgroundColor;
    customTextUnderlayStyles['backgroundColor'] = backgroundColor;

    if (backgroundUri) {
      customComponentStyles['backgroundImage'] = 'url(' + backgroundUri + '?auto=webp&disable=upscale&width=1480)';
    }

    /* Breakpoints: XS: 0-767, S: 768-991, M: 992-1199, L: 1200-1469, XL: 1470-  */
    const imageBreakpoints = [
      { "maxWidth": 640, "widthPX": 300 },
      { "maxWidth": 768, "widthPX": 300 },
      { "maxWidth": 992, "widthPX": 366 },
      { "maxWidth": 1200, "widthPX": 272 },
      {                   "widthPX": 272 },
    ];

    return (
      <div className="MeteorologistForecast" style={customComponentStyles}>
        { forecastData.length ?
          <div className="MeteorologistForecast-textCell">
            <div className="MeteorologistForecast-textContainer">
              <ComponentTitle title={forecastTitle} showArrow={false} classes="MeteorologistForecast-forecastTitle" color={textColor} titleType={titleType}/>
              <Timestamp publishDate={pubDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
              <div className="MeteorologistForecast-forecastDetails" onClick={() => this._setModalVisible() }>
                <div className="MeteorologistForecast-forecastTextContainer">
                  <ul className="MeteorologistForecast-forecastText">
                    <li
                      dangerouslySetInnerHTML={{__html: unescapedSanitizedDescription }}
                    />
                  </ul>
                </div>
                <div className="readMore">Read more</div>
                <div className="MeteorologistForecast-forecastDetailsUnderlay" style={customTextUnderlayStyles}/>
              </div>
            </div>
          </div> : null }
        <div className={'MeteorologistForecast-profileCell' + (meteorologistImage ? ' MeteorologistForecast-profileCell--hasImage' : '')}>
          { meteorologistImage ?
            <div className="MeteorologistForecast-profileImage">
              <Image imageBreakpoints={imageBreakpoints} src={meteorologistImage} alt={'Image of ' + (name || 'meteorologist')} />
            </div> : null }
          { weatherVideo ?
            <div className="MeteorologistForecast-videoIcon hidden-xs">
              <VideoThumbnail
                title={weatherVideo['media:title']}
                clipId={weatherVideo['wn:clipid']}
                thumbnailUri={_.get(weatherVideo['media:thumbnail'], '[0]["@url"]', null)}
                autoPlay={autoPlayVideo} />
            </div>
            : null
          }
          { weatherVideo ?
            <div className="MeteorologistForecast-videoIcon visible-xs-block hidden-sm">
              <Video clipId={weatherVideo['wn:clipid']} />
            </div>
            : null
          }
          <div className={profileInfoClassNames}>
            <div className="MeteorologistForecast-name">
              {name || ''}
            </div>
            <div className="MeteorologistForecast-meteorologistTitle">
              {meteorologistTitle || ''}
            </div>
            <div className="MeteorologistForecast-socialLinks">
              {facebookInfo ? <SocialIcon iconType="facebook" destinationUri={facebookInfo} /> : null}
              {twitterInfo ? <SocialIcon iconType="twitter" destinationUri={twitterInfo} /> : null}
              {googleInfo ? <SocialIcon iconType="google" destinationUri={googleInfo} /> : null}
              {email ? <SocialIcon iconType="email" destinationUri={'mailto:' + email} noTarget /> : null}
            </div>
          </div>
        </div>

        {backgroundUri ? <div className="MeteorologistForecast-backgroundOverlay" /> : null}
        <MeteorologistForecastModal
          title={forecastTitle}
          forecastContent={unescapedSanitizedDescription}
          meteorlogistName={name}
          meteorologistTitle={meteorologistTitle}
          meteorologistImage={meteorologistImage}
          email={email}
          titleType={titleType}
          facebookInfo={facebookInfo}
          twitterInfo={twitterInfo}
          googleInfo={googleInfo}
          forecastDate={pubDate}
          showModal={showModal}
          onHideModal={this._setModalHidden.bind(this)}
          timestampOptions={timestampOptions}/>
      </div>
    );
  }
}

/**
 * Class representing the modal for the full meteorologist text
 */
class MeteorologistForecastModal extends Component {
  static propTypes = {
    title: PropTypes.string,
    forecastContent: PropTypes.string.isRequired,
    meteorlogistName: PropTypes.string,
    meteorologistTitle: PropTypes.string,
    meteorologistImage: PropTypes.string,
    email: PropTypes.string,
    facebookInfo: PropTypes.string,
    twitterInfo: PropTypes.string,
    googleInfo: PropTypes.string,
    forecastDate: PropTypes.string,
    showModal: PropTypes.bool.isRequired,
    onHideModal: PropTypes.func.isRequired,
    timestampOptions: PropTypes.shape({
      showElapsedTime: PropTypes.bool,
      displayShortDateTime: PropTypes.bool
    }),
    titleType: PropTypes.string
  }

  render() {
    const {
      title = '',
      forecastContent = '',
      meteorlogistName = '',
      meteorologistTitle = '',
      meteorologistImage = '',
      email = '',
      facebookInfo = '',
      twitterInfo = '',
      googleInfo = '',
      forecastDate = '',
      showModal,
      onHideModal,
      timestampOptions: {
        showElapsedTime,
        displayShortDateTime
      } = {},
      titleType = TITLE_TYPE.MODULE_TITLE
    } = this.props;

    return (
      <Modal className="MeteorologistForecastModal"
             show={showModal}
             onHide={onHideModal}
             backdrop
             aria-labelledby="contained-modal-title-lg">
        <Modal.Header className="MeteorologistForecastModal-header" closeButton />
        <Modal.Body>
          { title ? <ComponentTitle titleType={titleType} title={title} classes="MeteorologistForecastModal-title" /> : null }
          <Timestamp publishDate={forecastDate} showElapsedTime={showElapsedTime} displayShortDateTime={displayShortDateTime}/>
          <div className="MeteorologistForecastModal-profileCell">
            <div className="MeteorologistForecastModal-profileImage">
              <Image src={meteorologistImage} alt={'Image of ' + (meteorlogistName || 'meteorologist')} width={60} />
            </div>
            <div className="MeteorologistForecastModal-profileInfo">
              <div className="MeteorologistForecastModal-name">
                {meteorlogistName}
              </div>
              <div className="MeteorologistForecastModal-meteorologistTitle">
                {meteorologistTitle}
              </div>
              <div className="MeteorologistForecastModal-socialLinks">
                {facebookInfo ? <SocialIcon iconType="facebook" destinationUri={facebookInfo} /> : null}
                {twitterInfo ? <SocialIcon iconType="twitter" destinationUri={twitterInfo} /> : null}
                {googleInfo ? <SocialIcon iconType="google" destinationUri={googleInfo} /> : null}
                {email ? <SocialIcon iconType="email" destinationUri={'mailto:' + email} noTarget /> : null}
              </div>
            </div>
          </div>
          <div className="MeteorologistForecastModal-forecastContent" dangerouslySetInnerHTML={{__html: forecastContent }} />
        </Modal.Body>
      </Modal>
    );
  }
}

export default MeteorologistForecast;



// WEBPACK FOOTER //
// ./app/components/weather/MeteorologistForecast.js